import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import { Collapse } from "@mui/material";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import Icons from "../../Constants/Icons";

// All User sub-routes — used to highlight the "User" sidebar item
const USER_ROUTES = [
  "/ArtLover",
  "/Artist",
  "/ArtistPro",
  "/ArtRunner",
  "/ArtLoverOnly",
  "/ReferralManagement",
];

const ListScreenData = [
  {
    text: "Dashboard",
    icon: Icons.ic_grey_home,
    iconWhite: Icons.ic_home,
    route: "/",
  },
  {
    text: "Art Category",
    icon: Icons.Ic_Category,
    iconWhite: Icons.Ic_Category_1,
    route: "/ArtCategory",
  },
  {
    text: "Artistic Tag",
    icon: Icons.ic_new_user_value_grey,
    iconWhite: Icons.ic_new_user_value,
    route: "/ArtisticTag",
  },
  {
    text: "New Registration",
    icon: Icons.ic_user,
    iconWhite: Icons.ic_user_white,
    route: "/Registration",
  },
  {
    text: "User Application",
    icon: Icons.ic_user,
    iconWhite: Icons.ic_user_white,
    route: "/UserApplication",
  },
  {
    text: "User",
    icon: Icons.ic_user,
    route: "/ArtLover",
    iconWhite: Icons.ic_user_white,
    arrowIcon: Icons.ic_down,
    subItems: [
      { text: "Referral User", route: "/ReferralManagement" },
    ],
  },
  {
    text: "Wallet Funds",
    icon: Icons.ic_wallet,
    iconWhite: Icons.ic_white_wallet,
    route: "/walletFunds",
  },
  {
    text: "Booked Events",
    icon: Icons.Ic_event,
    iconWhite: Icons.ic_user_white,
    route: "/BookedEvents",
  },
  {
    text: "Achievements",
    icon: Icons.ic_grey_achievement,
    iconWhite: Icons.ic_white_achievement,
    route: "/Achievements",
  },
  {
    text: "Events",
    icon: Icons.Ic_event,
    route: "/EventsManagement",
    iconWhite: Icons.event,
    arrowIcon: Icons.ic_down,
    subItems: [
      { text: "Master Event", route: "/MasterEvents" },
      { text: "Admin Event", route: "/AdminEvents" },
      { text: "Submited Event", route: "/SubmitedEvent" },
    ],
  },
  {
    text: "Subscription",
    icon: Icons.ic_user,
    iconWhite: Icons.ic_user_white,
    route: "/PlanList",
  },
];

const CustomSideBar = () => {
  const [selectedItem, setSelectedItem] = useState("/");
  const [selectedSubItem, setSelectedSubItem] = useState("");
  const [openSubItems, setOpenSubItems] = useState({});
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;

    // If current path is a User sub-route, highlight "User" item
    if (USER_ROUTES.includes(currentPath)) {
      setSelectedItem("/ArtLover");
      // Open User subItems if on ReferralManagement
      if (currentPath === "/ReferralManagement") {
        setOpenSubItems({ "/ArtLover": true });
        setSelectedSubItem("/ReferralManagement");
      } else {
        setOpenSubItems({});
        setSelectedSubItem("");
      }
      return;
    }

    const matchedItem = ListScreenData.find((item) => item.route === currentPath);
    if (matchedItem) {
      setSelectedItem(matchedItem.route);
      if (matchedItem.subItems) {
        setOpenSubItems({ [matchedItem.route]: true });
      } else {
        setOpenSubItems({});
      }
    } else {
      // Check if current path is a subItem
      const matchedSubItem = ListScreenData.flatMap((item) => item.subItems || []).find(
        (subItem) => subItem.route === currentPath
      );
      if (matchedSubItem) {
        setSelectedSubItem(matchedSubItem.route);
        const parentItem = ListScreenData.find(
          (item) => item.subItems && item.subItems.some((sub) => sub.route === matchedSubItem.route)
        );
        if (parentItem) {
          setSelectedItem(parentItem.route);
          setOpenSubItems({ [parentItem.route]: true });
        }
      }
    }
  }, [location.pathname]);

  const handleItemClick = (item) => {
    setSelectedItem(item.route || item);

    // Toggle subItems open/close
    if (item.subItems) {
      setOpenSubItems((prev) => ({
        ...prev,
        [item.route]: !prev[item.route],
      }));
    } else {
      setOpenSubItems({});
      setSelectedSubItem("");
    }
  };

  const handleSubItemClick = (subItem, parentRoute) => {
    setSelectedSubItem(subItem.route);
    // Keep parent open
    setOpenSubItems((prev) => ({
      ...prev,
      [parentRoute]: true,
    }));
  };

  const isSubItemOpen = (route) => {
    return openSubItems[route] === true;
  };

  useEffect(() => {
    const eventListener = (event) => {
      const navLink = event.detail;
      const item = ListScreenData.find((item) => item.route === navLink);
      if (item) {
        handleItemClick(item);
      }
    };
    window.addEventListener("tabEventListener", eventListener);
    return () => window.removeEventListener("tabEventListener", eventListener);
  }, []);

  useEffect(() => {
    const eventListener = (event) => {
      const navLink = event.detail;
      setSelectedItem("/EventsManagement");
      setOpenSubItems({ "/EventsManagement": true });
      setSelectedSubItem(navLink);
    };
    window.addEventListener("subTabEventListner", eventListener);
    return () => window.removeEventListener("subTabEventListner", eventListener);
  }, []);

  useEffect(() => {
    const eventListener = (event) => {
      const navLink = event.detail;
      setSelectedItem("/ArtLover");
      setOpenSubItems({ "/ArtLover": true });
      setSelectedSubItem(navLink);
    };
    window.addEventListener("subTabUserListner", eventListener);
    return () => window.removeEventListener("subTabUserListner", eventListener);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname;
      const currentPathGet = ListScreenData.filter((item) => item.route === currentPath);
      if (currentPathGet.length !== 0) {
        const item = currentPathGet[0];
        setSelectedItem(item.route);
        if (item.subItems) {
          setOpenSubItems({ [item.route]: true });
        } else {
          setOpenSubItems({});
        }
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <Drawer
      variant="permanent"
      className="Drawer-side-bar"
      sx={{
        width: "15%",
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: "15%",
          boxSizing: "border-box",
          background: "rgba(6, 101, 228, 0.2)",
        },
      }}
    >
      <Link to="/" className="drawer-link">
        <div className="logo-container">
          <img className="logo-image" src={Icons.ic_AY_logo} alt="new" />
          <div className="logo-text">Artfulyo</div>
        </div>
      </Link>
      <List className="drawer-list">
        {ListScreenData.map((item) => {
          const isSelected = selectedItem === item.route;
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isOpen = isSubItemOpen(item.route);

          return (
            <div key={item.text}>
              <ListItem>
                <ListItemButton
                  className={`list-item-button ${isSelected ? "active list-button-active" : ""}`}
                  component={NavLink}
                  to={item.route}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="list-item-content">
                    <img
                      className="list-item-icon"
                      src={isSelected ? item.iconWhite : item.icon}
                      alt="new"
                    />
                    <div className="list-item-text">{item.text}</div>
                    {hasSubItems && (
                      <img
                        className={`arrow-icon ${isOpen ? "valid-menu-round" : "menu-round"}`}
                        src={item.arrowIcon}
                        alt="new"
                      />
                    )}
                  </div>
                </ListItemButton>
              </ListItem>
              {hasSubItems && (
                <Collapse in={isOpen}>
                  {item.subItems.map((subItem, subIndex) => (
                    <ListItem key={subIndex} className="sub-list-item">
                      <ListItemButton
                        className={`sub-list-item-button ${selectedSubItem === subItem.route ? "subitem-active" : ""
                          }`}
                        component={NavLink}
                        to={subItem.route}
                        onClick={() => handleSubItemClick(subItem, item.route)}
                      >
                        <div className="sub-list-item-content" style={{ paddingLeft: "35px" }}>
                          <div className="sub-list-item-text">{subItem.text}</div>
                        </div>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
    </Drawer>
  );
};

export default CustomSideBar;